/**
 * Locsy Mitra dashboard card art — same hardcoded thumbs as the Flutter app.
 * Prefer webpack-bundled URLs so production always shows real images.
 */

import {
  MITRA_BUNDLED_WEBP,
  MITRA_CARD_KEY_TO_ASSET,
} from '../assets/mitra-card-defaults/index';

export const MITRA_DEFAULT_ASSET_BASE = `${process.env.PUBLIC_URL || ''}/mitra-card-defaults`;

function normalizeAssetName(hint) {
  return String(hint || '')
    .trim()
    .replace(/^.*[\\/]/, '');
}

/**
 * Resolve the bundled (or public) URL for a Mitra dashboard filename.
 * @param {string | null | undefined} hint e.g. catalog_manager.webp
 * @param {string | null | undefined} cardKey e.g. grow_catalog_manager
 */
export function mitraDefaultAssetUrl(hint, cardKey) {
  const fromKey = cardKey ? MITRA_CARD_KEY_TO_ASSET[cardKey] : null;
  const name = normalizeAssetName(hint) || fromKey;
  if (!name || name.includes('..')) return null;

  if (MITRA_BUNDLED_WEBP[name]) {
    return MITRA_BUNDLED_WEBP[name];
  }

  return `${MITRA_DEFAULT_ASSET_BASE}/${encodeURIComponent(name)}`;
}

/** Prefer custom CDN thumb; else hardcoded Mitra app asset for this card. */
export function resolveMitraCardPreviewUrl(card) {
  const custom = String(card?.thumbnail_url || '').trim();
  if (custom) return custom;
  return mitraDefaultAssetUrl(
    card?.default_asset_hint,
    card?.card_key || card?.key,
  );
}

/**
 * Full library of current Mitra app images, grouped for the admin picker.
 */
export const MITRA_ASSET_LIBRARY = {
  partner_services: [
    { file: 'catalog_manager.webp', label: 'Catalog Manager' },
    { file: 'ad_campaigns.webp', label: 'Ad Campaigns' },
    { file: 'deals_coupons_card.webp', label: 'Deals & Coupons' },
    { file: 'announcements_card.webp', label: 'Announcements' },
    { file: 'customer_leads.webp', label: 'Customer Leads' },
    { file: 'shop_polls_card.png', label: 'Customer Poll' },
    { file: 'loksy_wallet_card.png', label: 'Loksy Wallet' },
  ],
  store_profile: [
    { file: 'business_details_card.webp', label: 'Business Details' },
    { file: 'photos_update_card.webp', label: 'Photos Update' },
  ],
  catalog: [
    { file: 'catalog_manager.webp', label: 'Services Catalog' },
    { file: 'stock.webp', label: 'Inventory / Stock' },
  ],
  billing: [
    { file: 'new_bill_card.png', label: 'New Bill' },
    { file: 'draft_bills_card.webp', label: 'Draft Bills' },
    { file: 'view_bills_card.webp', label: 'View Bills' },
    { file: 'pending_works.webp', label: 'Pending Works' },
    { file: 'deals_coupons_card.webp', label: 'Coupons Management' },
    { file: 'quick_billing_card.webp', label: 'Quick Billing' },
    { file: 'catalog_billing_card.webp', label: 'Catalog Billing' },
    { file: 'service_billing_card.webp', label: 'Service Billing' },
    { file: 'create_bill_card.webp', label: 'Create Bill' },
  ],
  leads_inbox: [
    { file: 'customer_leads.webp', label: 'Locsy Leads / Inbox' },
    { file: 'user_postings.webp', label: 'User Postings' },
    { file: 'citizen_posting_categories.webp', label: 'Posting Categories' },
  ],
  reports: [
    { file: 'today_sales.webp', label: "Today's Sales" },
    { file: 'leads_stats_card.webp', label: 'Leads Stats' },
  ],
  students: [
    { file: 'digital_attendance.webp', label: 'Digital Attendance' },
  ],
  quick_access: [
    { file: 'quick_add_product.webp', label: 'Quick Add Product' },
    { file: 'quick_create_ad.webp', label: 'Quick Create Ad' },
    { file: 'quick_add_coupon.webp', label: 'Quick Add Coupon' },
    { file: 'quick_shop_photos.webp', label: 'Quick Shop Photos' },
  ],
  banners: [
    { file: 'grow_business_banner.webp', label: 'Grow Business Banner' },
    { file: 'customers.webp', label: 'Customers' },
    { file: 'expenses.webp', label: 'Expenses' },
  ],
};

export const MITRA_ASSET_LIBRARY_LABELS = {
  partner_services: 'Connect with Loksy · Partner Services',
  store_profile: 'Connect with Loksy · Store Profile',
  catalog: 'My Business Desk · Products',
  billing: 'My Business Desk · Bills',
  leads_inbox: 'My Business Desk · Leads',
  reports: 'My Business Desk · Reports',
  students: 'My Business Desk · Students',
  quick_access: 'Quick Access chips (square)',
  banners: 'Banners & other',
};
